import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import uniqBy from 'lodash/uniqBy';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PageError from 'pages/404';
import { useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { KEY_ADS_POSITION } from 'src/components/ads/dynamic-ads';
import Layout from 'src/components/layouts';
import { APP_IMG, APP_URL, IS_NO_ROBOT_INDEX, SHOW_COMMEMORATE } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
import { escapeChar, isExistAds, rewriteTxt, timestamp } from 'src/utils/helper';
import { GET_ARTICLES } from 'src/utils/services';
import styled from 'styled-components';
// import { getSession } from '@auth0/nextjs-auth0';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import useUser from 'src/utils/useUser';

const CardH = dynamic(import('src/components/cards/card-h'));
const CardFullV = dynamic(import('src/components/cards/card-full-v'));
const CardV = dynamic(import('src/components/cards/card-v'));
const DynamicAds = dynamic(import('src/components/ads/dynamic-ads'));
const SectionAffiliate = dynamic(import('src/components/sections/section-event/section-affiliate'));

const PageCategory = ({ articles, nextArticles, category, subCategory, lastNews, meta, ads, dataAffiliate }) => {
  const router = useRouter();
  const data = useUser();
  const sessionSevId = data?.user ? decryptionClient(data?.user) : null;
  if (isEmpty(articles)) return <PageError ads={ads} sessionSevId={sessionSevId} />;

  let setItemListElement = '';
  const baseUrlCategory = `${APP_URL}${router.asPath}`;

  /* -------------------------------- START : LOADMORE -------------------------------- */

  const nextLinkRef = useRef(nextArticles);
  const listArticlesRef = useRef(articles);
  const [listArticles, setListArticles] = useState(articles);
  // const [isFetching, setIsFetching] = useState(false);
  // console.log('#3 outside', nextLinkRef.current);

  const handleLoad = async () => {
    try {
      if (nextArticles) {
        const resData = await GET_ARTICLES(`${nextLinkRef.current || ''}`, false, true);
        if (!isEmpty(resData?.data)) {
          nextLinkRef.current = resData?.nextTo || resData?.links?.next;
          const newLists = [...listArticlesRef.current, ...resData?.data];
          listArticlesRef.current = uniqBy(newLists, 'id');

          setListArticles(listArticlesRef.current);
        }
      }
    } catch (err) {
      console.error(`${timestamp()} ==========> LOAD_CATEGORY_LIST ERROR : ${nextLinkRef?.current}`, err);
    }
  };

  const _article = {
    title: meta?.title || WEB_TITLE,
    description: meta?.description || WEB_DESC,
    keyword: meta?.keyword || WEB_KEYWORD
  };
  const cloneArticles = [...listArticles];
  const firstData = cloneArticles.shift();
  const cloneBlockSide = lastNews;

  const articleDescriptionSeo = escapeChar(_article?.description) || WEB_DESC;

  if (subCategory.en != null) {
    // baseUrlCategory = `${APP_URL}/category/${category?.en}/${subCategory?.en}`;
    setItemListElement = `[
        {
          "@type": "ListItem",
          "position": 1,
          "name": "หน้าแรก",
          "item": "${APP_URL}"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "${category?.th}",
          "item": "${APP_URL}/category/${category?.en}"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "${subCategory?.th}",
          "item": "${baseUrlCategory}"
        }
      ]`;
  } else {
    // baseUrlCategory = `${APP_URL}/category/${category?.en}`;
    setItemListElement = `[
        {
          "@type": "ListItem",
          "position": 1,
          "name": "หน้าแรก",
          "item": "${APP_URL}"
        },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "${category?.th}",
            "item": "${APP_URL}/category/${category?.en}"
          }
        ]`;
  }
  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={_article?.title ? `ขอบสนาม - ${_article?.title}` : WEB_TITLE}
        description={_article?.description || WEB_DESC}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
        openGraph={{
          type: 'article',
          locale: 'th_TH',
          site_name: WEB_NAME,
          title: _article?.title ? `ขอบสนาม - ${_article?.title}` : WEB_TITLE,
          description: _article?.description || WEB_DESC,
          url: baseUrlCategory,
          images: [
            {
              url: IMAGE_DEFAULT,
              width: '1920',
              height: '1080',
              alt: WEB_NAME
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword || WEB_KEYWORD
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <meta property='twitter:title' content={_article?.title ? `ขอบสนาม - ${_article?.title}` : WEB_TITLE} />
        <meta property='twitter:description' content={_article?.description || WEB_DESC} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />

        {/* ------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                __th_page="${subCategory?.en ? subCategory.en : category?.en}";
              `
          }}
        />

        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "https://schema.org",
                  "@type": "WebSite",
                  "name": "${WEB_NAME}",
                  "url": "${WEB_URL.WEB}",
                  "description": "${articleDescriptionSeo}",
                  "sameAs": [
                    "${WEB_URL.FACEBOOK}",
                    "${WEB_URL.YOUTUBE}",
                    "${WEB_URL.INSTAGRAM}"
                  ],
                  "potentialAction": {
                    "@type": "SearchAction",
                    "target": "${WEB_URL.WEB}search?q={search_term_string}",
                    "query-input": "required name=search_term_string"
                  }
                }
              `
          }}
        />

        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "http://schema.org",
                    "@type": "WebPage",
                    "name": "${subCategory?.th ? subCategory.th : category?.th}",
                    "description": "${articleDescriptionSeo}",
                    "publisher": {
                      "@type": "ProfilePage",
                      "name": "${WEB_NAME}"
                    }
                  }
                `
          }}
        />

        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "http://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": ${setItemListElement}
                }
              `
          }}
        />
      </Head>
      {/* -------------------------------------------------------------------------- */}
      {!isEmpty(dataAffiliate) && <SectionAffiliate position={4} data={dataAffiliate} />}

      <Layout ads={ads} sessionSevId={sessionSevId}>
        <CategoryArea id='category-area' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : '' }}>
          <div className='category-area section-wrapper'>
            <div className='container'>
              {/* ---------------------------- BLOCKTOP : START --------------------------- */}
              <div className='breadcrumb-area'>
                <div className='breadcrumb-title'>
                  <h1 className='category m-0'>{subCategory.th ? subCategory.th : category?.th}</h1>
                  <h2 className='hide position-absolute'> ข่าวทั้งหมด </h2>
                  <ul>
                    <li>
                      <a
                        aria-label='index'
                        title='index'
                        href='/'
                        //! DATALAYER
                        // onClick={() =>
                        //   sendDataLayerGlobal({
                        //     key: KEY_EVENT.TRACK_EVENT,
                        //     event: KEY_EVENT.BREADCRUMB,
                        //     data: {
                        //       title: 'หน้าแรก',
                        //       link: '/'
                        //     }
                        //   })
                        // }
                      >
                        หน้าแรก
                      </a>
                    </li>
                    <li>ข่าวทั้งหมด</li>
                  </ul>
                </div>
                {/* ------------------------------ HEADING : END ----------------------------- */}
              </div>
              <div className='row' id='section1'>
                <div className='col-12 col-lg-8 d-block'>
                  {/* ------------------------------ HEADING : START ----------------------------- */}
                  {firstData ? (
                    <CardFullV
                      data={firstData}
                      path={firstData?.link}
                      //! DATALAYER
                      eventDataLayer={{
                        type: DATALAYER_TYPE.TRACK_POSITION,
                        router: router?.pathname,
                        section: 'ข่าวทั้งหมด',
                        position: 'ใต้:โฆษณาตำแหน่ง billboard',
                        data: {
                          block: '1',
                          title: firstData?.title,
                          heading: !isEmpty(subCategory?.th) ? subCategory?.th : category?.th
                        }
                      }}
                    />
                  ) : (
                    <div className='error-content'>
                      <img className='no-article' src={`${APP_IMG}/images/cropped-khobsanam-logo-1.png`} loading='lazy' alt='no-article' title='no-article' />
                    </div>
                  )}
                </div>

                {!isEmpty(cloneBlockSide) && (
                  <div className={`col-12 col-lg-4 pt-10 d-none d-lg-block ${!firstData ? 'show-mb' : ''}`}>
                    <h2 className='heading'>ข่าวล่าสุด</h2>
                    <div className='row'>
                      {cloneBlockSide.map((item, index) => (
                        <div key={index} className='col-12 col-md-6 col-lg-12 '>
                          <CardH
                            data={item}
                            path={item.link}
                            //! DATALAYER
                            eventDataLayer={{
                              type: DATALAYER_TYPE.TRACK_POSITION,
                              router: router?.pathname,
                              section: 'ข่าวล่าสุด',
                              position: 'ใต้:โฆษณาตำแหน่ง billboard',
                              data: {
                                block: '2',
                                title: item?.title,
                                heading: 'ข่าวล่าสุด',
                                index: index + 1
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {isExistAds(ads, KEY_ADS_POSITION.BILLBOARD) && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />}

              {/* ---------------------------- BLOCKTOP : END --------------------------- */}
              {listArticles.length > 1 && (
                <InfiniteScroll
                  className='infinite-scroll'
                  pageStart={0}
                  loadMore={handleLoad}
                  hasMore={true}
                  loader={<div className='fetching'> Loading... </div>}
                  threshold={2000}
                  // useWindow={false}
                >
                  {/* ---------------------------- BLOCKLIST : START --------------------------- */}
                  {!isEmpty(cloneArticles) && (
                    <div className='row block-list'>
                      {cloneArticles.map((item, index) => (
                        <div key={index} className='col-6 col-md-4 col-lg-3 card-v' data-aos='fade-up'>
                          <CardV
                            data={item}
                            path={item.link}
                            eventDataLayer={{
                              type: DATALAYER_TYPE.TRACK_POSITION,
                              router: router?.pathname,
                              section: 'ข่าวทั้งหมด',
                              position: 'ใต้:โฆษณาตำแหน่ง billboard',
                              data: {
                                title: firstData?.title,
                                heading: !isEmpty(subCategory?.th) ? subCategory?.th : category?.th,
                                index: index + 2
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </InfiniteScroll>
              )}

              {/* ---------------------------- BLOCKLIST : END --------------------------- */}
            </div>
          </div>
        </CategoryArea>
        {isExistAds(ads, KEY_ADS_POSITION.POSTARTICLE) && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
      </Layout>
    </>
  );
};

const CategoryArea = styled.section`
  .breadcrumb-title {
    position: relative;
    padding: 0 0 10px;
    margin: 0 0 20px;
    /* border-bottom: 2px solid #b9b9b9; */
    h1 {
      font-size: 28px;
      margin-bottom: 5px;
      @media (max-width: 768px) {
        text-align: center;
      }
    }
    ul {
      height: 25px;
      display: flex;
      list-style: none outside none;
      margin: 0;
      padding: 0;
      @media (max-width: 768px) {
        justify-content: center;
        align-items: center;
      }
      li {
        color: #494949;
        display: flex;
        align-items: center;
        margin-right: 5px;
        position: relative;
        font-size: 14px;
        &:after {
          content: '-';
          font-weight: 400;
          padding-left: 5px;
        }
      }
      li:last-child:after {
        content: '⚽';
      }
    }
  }
  .block-list {
    .card-v {
      margin-top: 20px;
    }
  }
  .card-h {
    .resolution-image {
      @media (max-width: 1024px) and (min-width: 767px) {
        position: absolute !important;
      }
    }
  }
`;

export async function getServerSideProps({ req, res, query }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    articles: null,
    category: null,
    subCategory: null,
    lastNews: null,
    related: null,
    meta: null,
    nextArticles: null,
    queryCategory: null,
    ads: [],
    // sessionSevId: {},
    dataAffiliate: []
  };

  initProps.queryCategory = query;
  let uri = `categories/${rewriteTxt(query?.cid)}`;
  // const _new = `${rewriteCid(query?.cid, query?.sub)}`;
  const regex = /([ก-๙])/;
  if (regex.test(query?.cid)) {
    return {
      redirect: {
        destination: '/',
        permanent: false
      }
    };
  }
  if (query?.sub) {
    // uri = `categories/${rewriteTxt(query?.cid)}/${query?.sub}`;
    if (rewriteTxt(query?.sub) === 'latest-news') {
      uri = `categories/${rewriteTxt(query?.cid)}`;
    } else if (rewriteTxt(query?.cid) === 'latest-news') {
      uri = `categories/${rewriteTxt(query?.cid)}`;
    } else {
      uri = `categories/${rewriteTxt(query?.cid)}/${query?.sub || ''}`;
    }
  }

  try {
    // console.log('CATEGORY', query);
    /* --------------------------------- MEMBER --------------------------------- */
    // const userSession = await getSession(req, res);
    // initProps.sessionSevId = userSession?.user || {};

    const regex = /([ก-๙])/;
    if (regex.test(query?.cid)) {
      res.setHeader('location', '/');
      res.statusCode = 301;
      res.end();
    }
    const resData = await GET_ARTICLES(`/api/v1.0/${uri}`, false, true);
    let resDataTo = null;
    if (resData?.statusCode === 404 || isUndefined(resData)) {
      // return {
      //   notFound: true
      // };
      res.setHeader('location', '/');
      res.statusCode = 301;
      res.end();
    }
    if (!isEmpty(resData)) {
      if (isEmpty(resData?.data)) {
        resDataTo = await GET_ARTICLES(resData?.nextTo, false, true);
        // console.log('#1', resDataTo);
        initProps.articles = resDataTo?.data || [];
      } else {
        initProps.articles = resData?.data || [];
      }
      initProps.nextArticles = resData?.nextTo || resData?.links?.next || null;
      initProps.ads = resData.ads || [];
      initProps.category = resData?.category;
      initProps.subCategory = resData?.subCategory;
      initProps.lastNews = resData?.last_news || [];
      initProps.related = resData?.related || [];
      initProps.meta = resData?.meta;
      initProps.dataAffiliate = resData?.affiliate || [];
    }
  } catch (err) {
    console.error(`${timestamp()} ==========> CATEGORY_LIST ERROR : `, err.message);
    initProps.errMessage = err?.message;
    initProps.statusCode = err?.response?.status || 500;
  }

  return {
    props: initProps
  };
}

export default PageCategory;
